import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { 
  Shield, Plus, Download, RefreshCw, Link2, 
  CheckCircle, ExternalLink, Eye, ArrowRightLeft,
  Hash, User, Calendar, FileText
} from "lucide-react";
import { API } from "../../apiConfig";


const CertificatesTab = ({ token }) => {
  const [certificates, setCertificates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chainStatus, setChainStatus] = useState(null);
  const [showMintDialog, setShowMintDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [mintForm, setMintForm] = useState({
    product_id: "",
    owner_name: "",
    owner_email: "",
    owner_type: "individual",
    location: "",
    notes: ""
  });
  const [transferForm, setTransferForm] = useState({
    new_owner_name: "",
    new_owner_email: "",
    new_owner_type: "individual",
    new_location: "",
    transfer_notes: ""
  });

  useEffect(() => {
    fetchCertificates();
    fetchProducts();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/admin/certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(response.data.certificates || []);
    } catch (error) {
      console.error("Error fetching certificates:", error);
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const validateChain = async () => {
    try {
      const response = await axios.get(`${API}/certificates/chain/validate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChainStatus(response.data);
      if (response.data.valid) {
        toast.success("Certificate chain verified!");
      } else {
        toast.error("Chain integrity issue detected!");
      }
    } catch (error) {
      console.error("Error validating chain:", error);
      toast.error("Failed to validate chain");
    }
  };

  const handleMint = async () => {
    if (!mintForm.product_id || !mintForm.owner_name) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/certificates/mint`,
        mintForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Certificate issued successfully!");
      setShowMintDialog(false);
      setMintForm({
        product_id: "",
        owner_name: "",
        owner_email: "",
        owner_type: "individual",
        location: "",
        notes: ""
      });
      fetchCertificates();
    } catch (error) {
      console.error("Error issuing certificate:", error);
      toast.error(error.response?.data?.detail || "Failed to issue certificate");
    }
  };

  const handleTransfer = async () => {
    if (!transferForm.new_owner_name || !selectedCert) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(
        `${API}/certificates/${selectedCert.certificate_id}/transfer`,
        transferForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Ownership transferred successfully!");
      setShowTransferDialog(false);
      setSelectedCert(null);
      setTransferForm({
        new_owner_name: "",
        new_owner_email: "",
        new_owner_type: "individual",
        new_location: "",
        transfer_notes: ""
      });
      fetchCertificates();
    } catch (error) {
      console.error("Error transferring certificate:", error);
      toast.error(error.response?.data?.detail || "Failed to transfer ownership");
    }
  };

  const downloadPDF = async (certificateId) => {
    try {
      const response = await axios.get(
        `${API}/certificates/${certificateId}/pdf`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate_${certificateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Certificate downloaded");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download certificate");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openTransferDialog = (cert) => {
    setSelectedCert(cert);
    setShowTransferDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="certificates-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Certificates of Authenticity
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage certificates of authenticity and provenance tracking
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={validateChain}
            className="rounded-xl"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Validate Chain
          </Button>
          <Dialog open={showMintDialog} onOpenChange={setShowMintDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-xl" data-testid="mint-cert-btn">
                <Plus className="h-4 w-4 mr-2" />
                Issue Certificate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Issue New Certificate</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Artwork *</Label>
                  <Select 
                    value={mintForm.product_id} 
                    onValueChange={(v) => setMintForm({...mintForm, product_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select artwork" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.product_id} value={product.product_id}>
                          {product.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Owner Name *</Label>
                  <Input
                    value={mintForm.owner_name}
                    onChange={(e) => setMintForm({...mintForm, owner_name: e.target.value})}
                    placeholder="Full name of owner"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Owner Email</Label>
                  <Input
                    type="email"
                    value={mintForm.owner_email}
                    onChange={(e) => setMintForm({...mintForm, owner_email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Owner Type</Label>
                  <Select 
                    value={mintForm.owner_type} 
                    onValueChange={(v) => setMintForm({...mintForm, owner_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="gallery">Gallery</SelectItem>
                      <SelectItem value="institution">Institution</SelectItem>
                      <SelectItem value="artist">Artist</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={mintForm.location}
                    onChange={(e) => setMintForm({...mintForm, location: e.target.value})}
                    placeholder="City, Country"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={mintForm.notes}
                    onChange={(e) => setMintForm({...mintForm, notes: e.target.value})}
                    placeholder="Optional notes about acquisition"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowMintDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleMint}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Issue Certificate
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Chain Status */}
      {chainStatus && (
        <div className={`rounded-xl border p-4 ${
          chainStatus.valid 
            ? "bg-emerald-500/10 border-emerald-500/30" 
            : "bg-destructive/10 border-destructive/30"
        }`}>
          <div className="flex items-center gap-3">
            {chainStatus.valid ? (
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            ) : (
              <Shield className="h-5 w-5 text-destructive" />
            )}
            <div>
              <p className="font-medium">
                {chainStatus.valid ? "Chain Integrity Verified" : "Chain Integrity Issue"}
              </p>
              <p className="text-sm text-muted-foreground">
                {chainStatus.message} • {chainStatus.total_certificates} certificates
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{certificates.length}</p>
              <p className="text-sm text-muted-foreground">Total Certificates</p>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">
                {certificates.filter(c => c.status === 'active').length}
              </p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Link2 className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">
                {certificates.length > 0 ? certificates[0].block_number + 1 : 0}
              </p>
              <p className="text-sm text-muted-foreground">Certificates Issued</p>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <ArrowRightLeft className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">
                {certificates.reduce((sum, c) => sum + (c.provenance?.length || 1) - 1, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Transfers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Block</TableHead>
              <TableHead>Token ID</TableHead>
              <TableHead>Artwork</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Loading certificates...
                </TableCell>
              </TableRow>
            ) : certificates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No certificates issued yet
                </TableCell>
              </TableRow>
            ) : (
              certificates.map((cert) => (
                <TableRow key={cert.certificate_id}>
                  <TableCell>
                    <Badge variant="outline">#{cert.block_number}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-amber-600 text-sm">
                      {cert.token_id}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{cert.artwork_title}</p>
                      <p className="text-sm text-muted-foreground">
                        {cert.artist_name}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p>{cert.current_owner_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {cert.provenance?.length || 1} owner(s)
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(cert.minted_at)}
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      cert.status === 'active' 
                        ? "bg-emerald-500/20 text-emerald-600 border-emerald-500/30"
                        : "bg-muted text-muted-foreground"
                    }>
                      {cert.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/certificate/${cert.certificate_id}`, '_blank')}
                        title="View Certificate"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadPDF(cert.certificate_id)}
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTransferDialog(cert)}
                        title="Transfer Ownership"
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Ownership</DialogTitle>
          </DialogHeader>
          {selectedCert && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Transferring</p>
                <p className="font-medium">{selectedCert.artwork_title}</p>
                <p className="text-sm">From: {selectedCert.current_owner_name}</p>
              </div>
              <div className="space-y-2">
                <Label>New Owner Name *</Label>
                <Input
                  value={transferForm.new_owner_name}
                  onChange={(e) => setTransferForm({...transferForm, new_owner_name: e.target.value})}
                  placeholder="Full name of new owner"
                />
              </div>
              <div className="space-y-2">
                <Label>New Owner Email</Label>
                <Input
                  type="email"
                  value={transferForm.new_owner_email}
                  onChange={(e) => setTransferForm({...transferForm, new_owner_email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Owner Type</Label>
                <Select 
                  value={transferForm.new_owner_type} 
                  onValueChange={(v) => setTransferForm({...transferForm, new_owner_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="gallery">Gallery</SelectItem>
                    <SelectItem value="institution">Institution</SelectItem>
                    <SelectItem value="artist">Artist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>New Location</Label>
                <Input
                  value={transferForm.new_location}
                  onChange={(e) => setTransferForm({...transferForm, new_location: e.target.value})}
                  placeholder="City, Country"
                />
              </div>
              <div className="space-y-2">
                <Label>Transfer Notes</Label>
                <Input
                  value={transferForm.transfer_notes}
                  onChange={(e) => setTransferForm({...transferForm, transfer_notes: e.target.value})}
                  placeholder="e.g., Sold at auction, Gift, etc."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransfer}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Transfer Ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificatesTab;
